import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const QUESTIONS = [
  {
    category: 'Lead response',
    question: 'How quickly do new leads usually get a personal response?',
    help: 'Speed matters most when the customer is actively comparing contractors.',
    options: [
      ['Usually the next day or later', 0],
      ['Later the same day', 1],
      ['Within about an hour', 2],
      ['Within about 15 minutes', 3],
    ],
  },
  {
    category: 'Lead response',
    question: 'Does every open lead have a clear next action?',
    help: 'A lead without an owner, date or next step is easy to lose.',
    options: [
      ['Rarely', 0],
      ['For some leads', 1],
      ['For most leads', 2],
      ['Yes, consistently', 3],
    ],
  },
  {
    category: 'Quote follow-up',
    question: 'After you send an estimate, how consistently do you follow up?',
    help: 'The quote is not finished just because the price was sent.',
    options: [
      ['Usually only if the customer replies', 0],
      ['I follow up once', 1],
      ['I follow up a few times', 2],
      ['I use a repeatable follow-up process', 3],
    ],
  },
  {
    category: 'Quote follow-up',
    question: 'Can you see which open estimates need attention today?',
    help: 'A visible follow-up queue turns old quotes into an actionable pipeline.',
    options: [
      ['No', 0],
      ['Only by checking messages or notes', 1],
      ['Mostly', 2],
      ['Yes, in one place', 3],
    ],
  },
  {
    category: 'Pricing & margin',
    question: 'How repeatable is your pricing process?',
    help: 'Consistent rates make it easier to protect margin and explain the price.',
    options: [
      ['Mostly from memory or gut feel', 0],
      ['I reuse some old prices', 1],
      ['I use a pricing sheet or price book', 2],
      ['I use maintained rates and review profitability', 3],
    ],
  },
  {
    category: 'Pricing & margin',
    question: 'Before sending a quote, do you review the job for margin risk or missing scope?',
    help: 'Scope gaps, forgotten removal and underpriced extras can erase profit.',
    options: [
      ['Rarely', 0],
      ['Sometimes', 1],
      ['Usually', 2],
      ['Every quote has a review step', 3],
    ],
  },
  {
    category: 'Reviews & referrals',
    question: 'What happens after a successful job?',
    help: 'Completed work can create the next lead if the follow-through is consistent.',
    options: [
      ['Nothing systematic', 0],
      ['I ask occasionally', 1],
      ['I usually ask for a review', 2],
      ['I consistently ask for reviews and referrals', 3],
    ],
  },
  {
    category: 'Reviews & referrals',
    question: 'Do you have a way to reconnect with past customers?',
    help: 'Past customers can become referral sources and future work.',
    options: [
      ['No organized list', 0],
      ['I can find them if needed', 1],
      ['I keep an organized customer list', 2],
      ['I have a repeatable reactivation or referral process', 3],
    ],
  },
];

const CATEGORY_ACTIONS = {
  'Lead response': {
    icon: 'Zap',
    title: 'Tighten speed-to-lead first',
    description: 'Give every new inquiry a fast response and a visible next action. Start by reviewing new leads at set times each day and recording the next customer step before you leave the record.',
    path: '/leads',
    action: 'Open lead workflow',
  },
  'Quote follow-up': {
    icon: 'PhoneCall',
    title: 'Build a quote-rescue routine',
    description: 'Treat sent estimates as active opportunities. Give every quote a follow-up date, review the due queue daily and keep the conversation history beside the estimate.',
    path: '/follow-up',
    action: 'See follow-up workflow',
    publicPath: '/fence-estimate-follow-up',
    publicAction: 'Use the free follow-up guide',
  },
  'Pricing & margin': {
    icon: 'Calculator',
    title: 'Make pricing repeatable',
    description: 'Move rates out of memory and into a maintained price book. Review footage, gates, removal and assumptions before the estimate leaves your hands.',
    path: '/price-book',
    action: 'See pricing workflow',
    publicPath: '/fence-profit-margin-calculator',
    publicAction: 'Run free Profit Guard',
  },
  'Reviews & referrals': {
    icon: 'Star',
    title: 'Turn completed work into the next opportunity',
    description: 'Add a consistent completion step: confirm satisfaction, request a review and make it easy for happy customers to refer someone else.',
    path: '/jobs',
    action: 'See job workflow',
  },
};

function scoreLabel(score) {
  if (score >= 85) return ['Strong revenue discipline', 'Your process has a solid foundation. The next gains are likely to come from consistency, automation and measuring conversion by stage.'];
  if (score >= 65) return ['Good foundation, visible leaks', 'Several parts of the process are working, but one or two weak stages can still let qualified work go cold.'];
  if (score >= 40) return ['Revenue is slipping through the workflow', 'You have pieces of a process, but the handoffs between lead, quote and follow-up are inconsistent enough to lose otherwise winnable work.'];
  return ['Major revenue leaks', 'The biggest opportunity is not adding more tools. It is creating a repeatable path from inquiry to quote to follow-up to completed job.'];
}

export default function RevenueScorecard() {
  const { isAuthenticated } = useAuth();
  const [answers, setAnswers] = useState({});
  const answeredCount = Object.keys(answers).length;
  const complete = answeredCount === QUESTIONS.length;

  const result = useMemo(() => {
    if (!complete) return null;
    const categoryTotals = {};
    QUESTIONS.forEach((question, index) => {
      const current = categoryTotals[question.category] || { points: 0, max: 0 };
      current.points += answers[index] ?? 0;
      current.max += 3;
      categoryTotals[question.category] = current;
    });
    const categories = Object.entries(categoryTotals).map(([name, values]) => ({
      name,
      score: Math.round((values.points / values.max) * 100),
    }));
    const totalPoints = Object.values(answers).reduce((sum, value) => sum + value, 0);
    const totalScore = Math.round((totalPoints / (QUESTIONS.length * 3)) * 100);
    const weakest = [...categories].sort((a, b) => a.score - b.score)[0];
    return { totalScore, categories, weakest };
  }, [answers, complete]);

  const reset = () => {
    setAnswers({});
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const status = result ? scoreLabel(result.totalScore) : null;
  const weakestAction = result ? CATEGORY_ACTIONS[result.weakest.name] : null;
  const workspaceTarget = isAuthenticated ? weakestAction?.path || '/dashboard' : '/contractor/signup';

  return (
    <div className="min-h-svh bg-background text-foreground">
      <a href="#scorecard-start" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">
        Skip to scorecard
      </a>
      <header className="max-w-5xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span className="font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Contractor revenue scorecard</span>
        </nav>
      </header>

      <main className="max-w-5xl mx-auto px-4 pb-16 space-y-8">
        <section className="picketops-revenue-surface grid lg:grid-cols-[1.15fr_0.85fr] overflow-hidden rounded-2xl border border-primary/20 bg-card shadow-(--shadow-md)">
          <div className="p-6 sm:p-8 lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free contractor resource · About 3 minutes</p>
            <h1 className="mt-4 max-w-3xl text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">
              Where is your contracting business leaking revenue?
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Score the four stages that most often cost contractors otherwise winnable work: lead response, quote follow-up, pricing discipline, and post-job referrals.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              {['No signup required', 'Instant result', 'Action plan included'].map(item => (
                <span key={item} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-2">
                  <ApperIcon name="Check" size={15} className="text-primary" aria-hidden="true" />
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div className="border-t lg:border-t-0 lg:border-l border-primary/15 bg-primary/5 p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">What you will get</p>
            <div className="mt-5 space-y-5">
              {[
                ['Gauge', 'Revenue score', 'A 0–100 process score based on your answers.'],
                ['TriangleAlert', 'Biggest leak', 'The weakest stage in your current revenue workflow.'],
                ['ListChecks', 'Next action', 'A practical improvement to work on first.'],
              ].map(([icon, title, detail]) => (
                <div key={title} className="flex gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-background text-primary border border-border">
                    <ApperIcon name={icon} size={19} aria-hidden="true" />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold">{title}</h2>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="scorecard-start" aria-labelledby="scorecard-heading" className="space-y-5 scroll-mt-6">
          <div className="sticky top-3 z-20 rounded-xl border border-border bg-background/95 p-4 shadow-md backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your progress</p>
                <p className="mt-1 text-sm font-medium">{answeredCount} of {QUESTIONS.length} questions answered</p>
              </div>
              {answeredCount > 0 && <Button variant="outline" size="sm" onClick={reset}>Start over</Button>}
            </div>
            <Progress className="mt-3" value={(answeredCount / QUESTIONS.length) * 100} />
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Revenue leak scorecard</p>
            <h2 id="scorecard-heading" className="mt-2 text-3xl font-heading font-semibold">Score the way work moves through your business.</h2>
            <p className="mt-2 text-sm text-muted-foreground">Choose the answer that is true most of the time, not the answer you want the process to become.</p>
          </div>

          {QUESTIONS.map((question, index) => (
            <Card key={question.question} className={answers[index] !== undefined ? 'border-primary/30' : ''}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold tabular-nums">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">{question.category}</p>
                    <h3 className="mt-2 text-lg font-semibold">{question.question}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{question.help}</p>
                    <div role="radiogroup" aria-label={question.question} className="mt-4 grid sm:grid-cols-2 gap-2">
                      {question.options.map(([label, value]) => {
                        const selected = answers[index] === value;
                        return (
                          <button
                            key={label}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setAnswers(current => ({ ...current, [index]: value }))}
                            className={`min-h-12 rounded-md border px-4 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:bg-accent hover:text-accent-foreground'}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        {!complete && (
          <section className="rounded-xl border border-border bg-card p-6 text-center">
            <ApperIcon name="Gauge" size={28} className="mx-auto text-primary" aria-hidden="true" />
            <h2 className="mt-3 text-xl font-heading font-semibold">Your result appears after all eight answers.</h2>
            <p className="mt-2 text-sm text-muted-foreground">{QUESTIONS.length - answeredCount} {QUESTIONS.length - answeredCount === 1 ? 'question remains' : 'questions remain'}.</p>
          </section>
        )}

        {result && weakestAction && status && (
          <section aria-labelledby="result-heading" className="overflow-hidden rounded-2xl border border-primary/25 bg-card">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
              <div className="bg-primary/5 p-6 sm:p-8 flex flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Your PicketOps revenue score</p>
                <p className="mt-3 text-7xl font-bold tracking-tight tabular-nums">{result.totalScore}</p>
                <p className="text-sm text-muted-foreground">out of 100</p>
                <h2 id="result-heading" className="mt-5 text-2xl font-heading font-semibold">{status[0]}</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{status[1]}</p>
              </div>
              <div className="p-6 sm:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Your four stages</p>
                <div className="mt-4 divide-y divide-border">
                  {result.categories.map(category => (
                    <div key={category.name} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-4">
                      <div>
                        <p className="text-sm font-semibold">{category.name}</p>
                        <Progress className="mt-2" value={category.score} />
                      </div>
                      <p className="text-xl font-bold tabular-nums">{category.score}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-border p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                  <ApperIcon name={weakestAction.icon} size={23} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">Biggest opportunity · {result.weakest.name}</p>
                  <h3 className="mt-2 text-2xl font-heading font-semibold">{weakestAction.title}</h3>
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{weakestAction.description}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild>
                  <Link to={workspaceTarget}>
                    {isAuthenticated ? weakestAction.action : 'Create account'}
                    <ApperIcon name="ArrowRight" size={16} aria-hidden="true" />
                  </Link>
                </Button>
                {!isAuthenticated && weakestAction.publicPath && (
                  <Button variant="outline" asChild>
                    <Link to={weakestAction.publicPath}>{weakestAction.publicAction}</Link>
                  </Button>
                )}
                <Button variant="ghost" asChild>
                  <Link to="/fence-estimate-checklist">Estimate checklist</Link>
                </Button>
                <Button variant="ghost" onClick={reset}>Retake scorecard</Button>
              </div>
              {!isAuthenticated && <p className="mt-3 text-xs text-muted-foreground">Create your company profile, then request operator activation. Support confirms the trial and $49/month founding plan terms. Signup does not start billing.</p>}
            </div>
          </section>
        )}

        <section className="rounded-xl border border-border bg-muted/30 p-5">
          <h2 className="text-lg font-heading font-semibold">What this score means</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            This is a workflow self-assessment, not a financial forecast. It does not estimate lost dollars or guarantee additional revenue. Use it to identify where your current process is least consistent and choose the next operational improvement.
          </p>
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/contractor-revenue-scorecard', layout: 'public' };
